import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService, LoaderService } from '@common/util-foundation';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { ItemType } from '@sky/util-models';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { BasketApiService } from '../../services/basket-api.service';
import { BasketPartialState } from '../basket.reducer';
import * as DevicesActions from './../devices/devices.actions';
import * as DevicesSelectors from './../devices/devices.selectors';
import * as QuotesActions from './quotes.actions';
import * as QuotesSelectors from './quotes.selectors';

@Injectable()
export class QuotesEffects {
  loadQuotesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuotesActions.loadQuotesSuccess),
      map(({ quoteApiResult }) => {
        const skyBox = quoteApiResult.basket.items[0];
        const skyBoxApplianceDetails = skyBox.data.applianceDetails;

        return DevicesActions.addDevicesToBasketSuccess({
          devices: [
            {
              applianceId: skyBox.itemId,
              applianceCode: skyBoxApplianceDetails.applianceCode,
              brandCode: skyBoxApplianceDetails.brandCode,
              purchasePrice: skyBoxApplianceDetails.purchasePrice,
              purchaseMonth: skyBoxApplianceDetails.purchaseMonth,
              purchaseYear: skyBoxApplianceDetails.purchaseYear,
              warranty: skyBoxApplianceDetails.warranty,
              ltPrice: skyBoxApplianceDetails.ltPrice,
              skyMobilePriceConfig: skyBoxApplianceDetails.skyMobilePriceConfig,
            },
          ],
        });
      })
    )
  );

  loadQuotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuotesActions.loadQuotes),
      switchMap((action) =>
        this.basketApiService
          .createQuote({
            itemType: ItemType.QuoteSkyMobile,
            applianceCode: action.applianceCode,
          })
          .pipe(
            map((quoteApiResult) =>
              QuotesActions.loadQuotesSuccess({ quoteApiResult })
            ),
            catchError((error: HttpErrorResponse) => {
              this.handleError();
              return of(QuotesActions.loadQuotesFailure({ error }));
            })
          )
      )
    )
  );

  selectQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DevicesActions.addDevicesToBasketSuccess,
        DevicesActions.deleteDevicesFromBasketSuccess
      ),
      withLatestFrom(
        this.store$.pipe(select(DevicesSelectors.getAllDevices)),
        this.store$.pipe(select(QuotesSelectors.getAllQuotes)),
        this.store$.pipe(select(QuotesSelectors.getLtMaxItems))
      ),
      switchMap(([, devices, quotes, ltMaxItems]) => {
        if (quotes) {
          const quote = quotes.find((q) => q.numberOfItems >= devices.length);
          return quote
            ? of(QuotesActions.selectQuote({ quote: { ...quote, ltMaxItems } }))
            : EMPTY;
        } else {
          return EMPTY;
        }
      })
    )
  );

  showLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QuotesActions.loadQuotes),
        tap(() => this.loaderService.showLoader())
      ),
    { dispatch: false }
  );

  hideLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          QuotesActions.loadQuotesSuccess,
          QuotesActions.loadQuotesFailure
        ),
        tap(() => this.loaderService.hideLoader())
      ),
    { dispatch: false }
  );

  handleError() {
    this.errorService.handleError();
  }

  constructor(
    private actions$: Actions,
    private store$: Store<BasketPartialState>,
    private readonly errorService: ErrorService,
    private basketApiService: BasketApiService,
    private loaderService: LoaderService
  ) {}
}
